import { useState } from "react";
import {
  Bell,
  Mail,
  Send,
  Loader2,
  Zap,
  TrendingDown,
  TrendingUp,
  CheckCircle,
} from "lucide-react";
import { Input } from "../components/ui/input";
import { Switch } from "../components/ui/switch";
import { Slider } from "../components/ui/slider";
import axios from "axios";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const alertTypeConfig = [
  { id: "high_margin", label: "Haute Marge", icon: Zap, color: "text-[#00E676]" },
  { id: "price_drop", label: "Baisse Prix", icon: TrendingDown, color: "text-blue-500" },
  { id: "trending", label: "Tendance", icon: TrendingUp, color: "text-yellow-500" },
];

export default function AlertSettings() {
  const [email, setEmail] = useState("");
  const [emailAlertTypes, setEmailAlertTypes] = useState(["high_margin", "price_drop", "trending"]);
  const [emailMinMargin, setEmailMinMargin] = useState(30);
  const [emailSubmitting, setEmailSubmitting] = useState(false);
  const [emailDone, setEmailDone] = useState(false);

  const [telegramUsername, setTelegramUsername] = useState("");
  const [telegramAlertTypes, setTelegramAlertTypes] = useState(["high_margin", "price_drop", "trending"]);
  const [telegramMinMargin, setTelegramMinMargin] = useState(30);
  const [telegramSubmitting, setTelegramSubmitting] = useState(false);
  const [telegramDone, setTelegramDone] = useState(false);

  const toggleAlertType = (types, setTypes, type) => {
    if (types.includes(type)) {
      setTypes(types.filter((t) => t !== type));
    } else {
      setTypes([...types, type]);
    }
  };

  const handleEmailSubscribe = async (e) => {
    e.preventDefault();
    if (!email) return;
    setEmailSubmitting(true);
    try {
      await axios.post(`${API}/alerts/subscribe/email`, {
        email,
        alert_types: emailAlertTypes,
        min_margin_percent: emailMinMargin,
      });
      toast.success("Vous êtes abonné aux alertes email !");
      setEmailDone(true);
    } catch (error) {
      toast.error("Erreur lors de l'abonnement");
    } finally {
      setEmailSubmitting(false);
    }
  };

  const handleTelegramSubscribe = async (e) => {
    e.preventDefault();
    if (!telegramUsername) return;
    setTelegramSubmitting(true);
    try {
      await axios.post(`${API}/alerts/subscribe/telegram`, {
        chat_id: telegramUsername,
        alert_types: telegramAlertTypes,
        min_margin_percent: telegramMinMargin,
      });
      toast.success("Vous êtes abonné aux alertes Telegram !");
      setTelegramDone(true);
    } catch (error) {
      toast.error("Erreur lors de l'abonnement Telegram");
    } finally {
      setTelegramSubmitting(false);
    }
  };

  return (
    <div className="space-y-8" data-testid="alert-settings-page">
      {/* Header */}
      <div>
        <h1 className="text-4xl sm:text-5xl font-extrabold tracking-tight flex items-center gap-3">
          <Bell className="text-[#00E676]" size={40} />
          NOTIFICATIONS
        </h1>
        <p className="text-muted-foreground mt-2 uppercase tracking-wider text-sm">
          Recevez des alertes sur les meilleures opportunités
        </p>
      </div>

      {/* Subscription Forms */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        {/* Email */}
        <div className="neo-card p-6">
          <h2 className="text-base md:text-lg font-bold uppercase tracking-tight mb-6 flex items-center gap-2">
            <Mail size={24} />
            Alertes Email
          </h2>

          {emailDone ? (
            <div className="flex flex-col items-center gap-4 py-8">
              <CheckCircle className="text-[#00E676]" size={48} />
              <p className="font-bold text-lg text-center">Abonnement email confirmé !</p>
              <p className="text-sm text-muted-foreground text-center">
                Vous recevrez les alertes à {email}
              </p>
              <button
                onClick={() => { setEmailDone(false); setEmail(""); }}
                className="neo-button-secondary text-sm"
              >
                Modifier
              </button>
            </div>
          ) : (
            <form onSubmit={handleEmailSubscribe} className="space-y-6">
              <div>
                <label className="text-sm font-bold uppercase tracking-wider block mb-2">
                  Adresse Email
                </label>
                <Input
                  type="email"
                  placeholder="votre@email.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="neo-input w-full"
                  data-testid="email-input"
                />
              </div>

              <div>
                <label className="text-sm font-bold uppercase tracking-wider block mb-3">
                  Types d'alertes
                </label>
                <div className="space-y-2">
                  {alertTypeConfig.map(({ id, label, icon: Icon, color }) => (
                    <div
                      key={id}
                      className="flex items-center justify-between p-3 border-2 border-[#0A0A0A] dark:border-[#00E676] cursor-pointer hover:bg-[#00E676]/10"
                      onClick={() => toggleAlertType(emailAlertTypes, setEmailAlertTypes, id)}
                    >
                      <div className="flex items-center gap-2">
                        <Icon className={color} size={18} />
                        <span className="font-bold">{label}</span>
                      </div>
                      <Switch checked={emailAlertTypes.includes(id)} />
                    </div>
                  ))}
                </div>
              </div>

              <div>
                <label className="text-sm font-bold uppercase tracking-wider block mb-3">
                  Marge minimum : {emailMinMargin}%
                </label>
                <Slider
                  value={[emailMinMargin]}
                  onValueChange={(val) => setEmailMinMargin(val[0])}
                  min={10}
                  max={60}
                  step={5}
                  className="w-full"
                />
                <p className="text-xs text-muted-foreground mt-2">
                  Uniquement les alertes avec marge &gt; {emailMinMargin}%
                </p>
              </div>

              <button
                type="submit"
                disabled={emailSubmitting || !email}
                className="neo-button-primary w-full flex items-center justify-center gap-2"
                data-testid="subscribe-email-btn"
              >
                {emailSubmitting ? (
                  <Loader2 className="animate-spin" size={20} />
                ) : (
                  <Mail size={20} />
                )}
                S'abonner par Email
              </button>
            </form>
          )}
        </div>

        {/* Telegram */}
        <div className="neo-card p-6">
          <h2 className="text-base md:text-lg font-bold uppercase tracking-tight mb-6 flex items-center gap-2">
            <Send size={24} />
            Alertes Telegram
          </h2>

          {telegramDone ? (
            <div className="flex flex-col items-center gap-4 py-8">
              <CheckCircle className="text-[#00E676]" size={48} />
              <p className="font-bold text-lg text-center">Abonnement Telegram confirmé !</p>
              <p className="text-sm text-muted-foreground text-center">
                Vous recevrez les alertes sur Telegram
              </p>
              <button
                onClick={() => { setTelegramDone(false); setTelegramUsername(""); }}
                className="neo-button-secondary text-sm"
              >
                Modifier
              </button>
            </div>
          ) : (
            <form onSubmit={handleTelegramSubscribe} className="space-y-6">
              <div className="bg-[#00E676]/10 border-2 border-[#00E676] p-4 mb-2">
                <p className="text-sm font-bold mb-2">1. Ouvrez notre bot Telegram :</p>
                <a
                  href="https://t.me/Profimy_Bot"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="neo-button-primary inline-flex items-center gap-2 text-sm px-4 py-2"
                  data-testid="telegram-bot-link"
                >
                  <Send size={16} />
                  Ouvrir @Profimy_Bot
                </a>
                <p className="text-sm font-bold mt-4 mb-1">2. Envoyez <code className="bg-[#0A0A0A] text-[#00E676] px-2 py-0.5">/start</code> au bot</p>
                <p className="text-sm font-bold mt-2 mb-1">3. Entrez votre identifiant ci-dessous :</p>
              </div>

              <div>
                <Input
                  type="text"
                  placeholder="Votre @username ou Chat ID"
                  value={telegramUsername}
                  onChange={(e) => setTelegramUsername(e.target.value)}
                  className="neo-input w-full"
                  data-testid="telegram-input"
                />
              </div>

              <button
                type="submit"
                disabled={telegramSubmitting || !telegramUsername}
                className="neo-button-primary w-full flex items-center justify-center gap-2"
                data-testid="subscribe-telegram-btn"
              >
                {telegramSubmitting ? (
                  <Loader2 className="animate-spin" size={20} />
                ) : (
                  <Send size={20} />
                )}
                S'abonner aux alertes Telegram
              </button>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}
